import { Component, Input, OnInit, TemplateRef } from '@angular/core';
import { FormatConfigEntry } from '../../core/base-api';
import { FormatConfigService } from '../../core/services/format-config.service';
import { PlatformLocation } from '@angular/common';
import { ComponentLocationStrategy } from '../services/location-strategy/component-location-strategy';
import { AbstractComponent } from '../components/abstract-component/abstract.component';

/**
 * Extend this Component if you need a Component which allows different Formats including Style (ng-templates).
 * The Format Type and Values are configured externally on the Server @link{AppFormatConfigService} - This component
 * handles the lookup to ng-Templates.
 *
 * @WhatItDoes: Maps a FormatConfigEntry.ElementEnum to an NgTemplate.
 */
@Component({
  // Seit Angular 9 braucht es hier @Component, damit im JIT modus @Input korrekt funktioniert, see https://angular.io/guide/migration-undecorated-classes
  template: '',
  standalone: false
})
export abstract class KompaktdarstellungTemplateComponent extends AbstractComponent implements OnInit {
  constructor(
    protected formatService: FormatConfigService,
    protected configType: FormatConfigEntry.TypeEnum,
    protected location: PlatformLocation,
    protected componentLocation: ComponentLocationStrategy
  ) {
    super();
    this.templateMapping = new Map();
    const path = location.pathname.substring(location.pathname.indexOf('/', 1) + 1);
    let predefinedFormat: FormatConfigEntry;
    this.addSub(
      this.formatService.formatConfig$.subscribe((config) => {
        predefinedFormat = config?.getFormat(this.configType, path, componentLocation.componentId());
      })
    );
    if (predefinedFormat && predefinedFormat.element) {
      this.templateFormatElement = predefinedFormat.element;
    }
  }

  /**
   * Use this Accessor to get the configured Template in the HTML
   * @returns {TemplateRef<any>}
   */
  public get templateElement() {
    return this.configuredTemplate;
  }

  @Input() format: FormatConfigEntry.ElementEnum;

  private templateMapping: Map<FormatConfigEntry.ElementEnum, TemplateRef<any>>;

  protected templateFormatElement: FormatConfigEntry.ElementEnum;

  protected configuredTemplate: TemplateRef<any>;

  abstract getDefaultTemplate(): TemplateRef<any>;

  abstract registerTemplates();

  ngOnInit(): void {
    this.registerTemplates();
    if (this.format) {
      this.configuredTemplate = this.getTemplate(this.format);
    } else {
      this.configuredTemplate = this.lookupTemplate(this.templateFormatElement);
    }
  }

  protected lookupTemplate(element: FormatConfigEntry.ElementEnum): TemplateRef<any> {
    const configuredTemplate = this.getTemplate(element);
    if (configuredTemplate) {
      return configuredTemplate;
    }
    return this.getDefaultTemplate();
  }

  protected getTemplate(element: FormatConfigEntry.ElementEnum) {
    return this.templateMapping.get(element);
  }

  /**
   * Use this Method to map Format-Elements (Config/Server) to Templates (Html)
   * @param {TemplateRef<any>} template
   * @param {FormatConfigEntry.ElementEnum} format
   */
  protected bindTemplates(template: TemplateRef<any>, format: FormatConfigEntry.ElementEnum) {
    this.templateMapping.set(format, template);
  }

  protected formatMatches(formats: FormatConfigEntry.ElementEnum[]): boolean {
    return formats.includes(this.format);
  }
}
